import type { DBBackupData } from '@sb/feathers-types';

import { getService } from '../utils';

/**
 * Enough for a USB database dump. A complete cloud backup needs far longer —
 * see CLOUD_BACKUP_TIMEOUT_MS.
 */
const DEFAULT_TIMEOUT_MS = 20_000;

/**
 * A complete backup streams the database, configuration, blob store and code
 * blocks to the cloud one artifact at a time, each with its own 15-minute cap
 * on the box. Timing out the socket early would report a failure
 * to the operator while feathers-bot carried on and registered the backup, so
 * this sits above the slowest capture rather than above the typical one.
 */
export const CLOUD_BACKUP_TIMEOUT_MS = 20 * 60 * 1000;

type CreateBackupParams = {
  isCloudBackup?: boolean;
  /**
   * Capture whole robot state plus a manifest, rather than a database dump
   * alone. Cloud backups taken this way register in the checkpoint index and
   * become restore points.
   */
  isCompleteBackup?: boolean;
  /** Operator-supplied name, surfaced wherever the backup is listed. */
  label?: string;
  timeoutMS?: number;
};

export async function createBackup(
  path: string,
  robotID: string,
  params?: CreateBackupParams,
): Promise<DBBackupData> {
  const dataService = getService('dataBackup')();
  dataService.timeout = params?.timeoutMS ?? DEFAULT_TIMEOUT_MS;

  const response = await dataService.create(
    { path, robotID, ...(params?.label ? { label: params.label } : {}) },
    {
      query: {
        cloud: !!params?.isCloudBackup,
        ...(params?.isCompleteBackup
          ? { complete: true, trigger: 'manual' }
          : {}),
      },
    },
  );

  return response;
}
